import React, { useState } from 'react';

import {
    makeStyles,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
} from '@material-ui/core';
import i18n from 'i18n-js';

import { ReservationDTO } from 'api/controllers/reservations';
import { NothingFoundMessage, SearchInput } from 'components/shared';
import { pagingOptions } from 'constants/arrays';

const EMPTY_TEXT_MAX_LENGTH = 100;

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minWidth: 0,
        minHeight: 0,
    },
    container: {
        flex: 1,
        minHeight: 0,
    },
    row: {
        cursor: 'pointer',
    },
    cell: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: 180,
    },
    empty: {
        height: 400,
        borderBottom: 'none',
    },
});

export interface ReservationColumn {
    id: string;
    label: string;
    getText: (reservation: ReservationDTO) => string;
    getSortValue?: (reservation: ReservationDTO) => string | number;
    render?: (reservation: ReservationDTO) => React.ReactNode;
}

interface ReservationsTableProps {
    columns: ReservationColumn[];
    reservations: ReservationDTO[];
    selectedId: string | null;
    onSelect: (id: string) => void;
    emptyText?: string;
}

export default function ReservationsTable({
    columns,
    reservations,
    selectedId,
    onSelect,
    emptyText,
}: ReservationsTableProps) {
    const classes = useStyles();
    const [orderBy, setOrderBy] = useState(columns[0].id);
    const [isAscending, setIsAscending] = useState(true);
    const [filters, setFilters] = useState<Record<string, string>>({});
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(pagingOptions[0]);

    const setFilter = (columnId: string, value: string) => {
        setFilters({ ...filters, [columnId]: value });
        setPage(0);
    };

    const handleSort = (columnId: string) => {
        setIsAscending(orderBy === columnId ? !isAscending : true);
        setOrderBy(columnId);
    };

    const sortColumn = columns.find((column) => column.id === orderBy) ?? columns[0];
    const sortValue = sortColumn.getSortValue ?? sortColumn.getText;
    const rows = reservations
        .filter((reservation) =>
            columns.every((column) =>
                column
                    .getText(reservation)
                    .toLowerCase()
                    .includes((filters[column.id] ?? '').toLowerCase())
            )
        )
        .sort((a, b) => {
            const left = sortValue(a);
            const right = sortValue(b);
            const order =
                typeof left === 'number' && typeof right === 'number'
                    ? left - right
                    : String(left).localeCompare(String(right));
            return isAscending ? order : -order;
        });
    const lastPage = Math.max(0, Math.ceil(rows.length / rowsPerPage) - 1);
    const currentPage = Math.min(page, lastPage);

    return (
        <div className={classes.root}>
            <TableContainer className={classes.container}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell
                                    key={column.id}
                                    sortDirection={isAscending ? 'asc' : 'desc'}
                                >
                                    <TableSortLabel
                                        active={orderBy === column.id}
                                        direction={isAscending ? 'asc' : 'desc'}
                                        onClick={() => handleSort(column.id)}
                                    >
                                        <SearchInput
                                            value={filters[column.id] ?? ''}
                                            name={column.label}
                                            trimValue={false}
                                            onChange={(value) => setFilter(column.id, value)}
                                            onEnter={() => undefined}
                                            onReset={() => setFilter(column.id, '')}
                                        />
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={columns.length} className={classes.empty}>
                                    <NothingFoundMessage
                                        text={emptyText}
                                        maxLength={EMPTY_TEXT_MAX_LENGTH}
                                    />
                                </TableCell>
                            </TableRow>
                        )}
                        {rows
                            .slice(currentPage * rowsPerPage, (currentPage + 1) * rowsPerPage)
                            .map((reservation) => (
                                <TableRow
                                    key={reservation.id}
                                    hover
                                    className={classes.row}
                                    selected={reservation.id === selectedId}
                                    onClick={() => onSelect(reservation.id)}
                                >
                                    {columns.map((column) => (
                                        <TableCell key={column.id} className={classes.cell}>
                                            {column.render
                                                ? column.render(reservation)
                                                : column.getText(reservation)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={pagingOptions}
                component="div"
                count={rows.length}
                rowsPerPage={rowsPerPage}
                labelRowsPerPage={i18n.t('common.rowsPerPage')}
                page={currentPage}
                onPageChange={(_event, nextPage) => setPage(nextPage)}
                onRowsPerPageChange={(event) => {
                    setRowsPerPage(Number(event.target.value));
                    setPage(0);
                }}
            />
        </div>
    );
}
